/* eslint-disable sort-keys */
import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ScopeProvider } from './scope';
import { RecoilRoot } from 'recoil';
import { BuildConfigProvider, SiteConfigProvider } from '../config';
import { InitLocalization, LocalizationProvider } from '../localization';
import { ThemeProvider } from './theme';
import { BrowserRouter } from 'react-router-dom';
import { PageLoadMetricsProvider } from './dd-custom-actions/PageLoadMetrics';
import { LogoutConfigProvider } from '../logout';
import { BillingHeaderProvider } from './billing-header';
import { AuthProvider } from '../auth';
import { CmsFeatureFlagProvider } from '../content';
import { FavoriteProductsProvider } from './favorite-products';
import { FeatureFlagProvider } from '../feature-flag';
import { HelmetProviderAsync } from '../content/helmet/HelmetProvider';
import { XvAppId } from '@im/ui-lib/mf-utils';
import { ErrorContent, ErrorContentBasic } from './scope/error-views';
import { StorefrontInfoProvider } from '../x4ec/provider/storefront-info';

export type ProviderConfigItem = {
  props?: Record<string, any>;
  provider: React.FC<any>;
};

type AppProviderConfig = {
  [appName: string]: ProviderConfigItem[];
};

type AppProviderProps = {
  basename?: string;
  envVars: any;
  mfOutlets?: any[];
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // for dev
      retry: 1, // default is 3
    },
  },
});

export const getAppProviders = (
  appId: XvAppId,
  { basename, envVars, mfOutlets = [] }: AppProviderProps
) => {
  const providerConfig: AppProviderConfig = {
    // Providers are lited in the order they are used in the app.
    // The first provider is the outermost provider, and the last provider is the innermost provider.
    // The order of the providers is important, as some providers depend on others.

    cep: [
      { provider: QueryClientProvider, props: { client: queryClient } },
      {
        provider: ScopeProvider,
        props: {
          scope: 'cep_core',
          fallback: <ErrorContentBasic appId={appId} />,
        },
      },
      { provider: RecoilRoot },
      {
        provider: BuildConfigProvider,
        props: { envVars, mfOutlets },
      },
      { provider: SiteConfigProvider },
      { provider: LocalizationProvider },
      {
        provider: ScopeProvider,
        props: {
          scope: 'cep_app',
          fallback: <ErrorContent variant="localized" />,
        },
      },
      { provider: ThemeProvider, props: { appId } },
      {
        provider: ScopeProvider,
        props: { scope: 'cep_app' },
      },
      { provider: BrowserRouter, props: { basename } },
      { provider: PageLoadMetricsProvider },
      { provider: AuthProvider },
      { provider: InitLocalization },
      { provider: LogoutConfigProvider },
      { provider: FeatureFlagProvider },
      { provider: CmsFeatureFlagProvider },
      { provider: HelmetProviderAsync },
      { provider: FavoriteProductsProvider },
      { provider: BillingHeaderProvider },
    ],

    x4ec: [
      { provider: QueryClientProvider, props: { client: queryClient } },
      {
        provider: ScopeProvider,
        props: {
          scope: 'x4ec_app',
          fallback: <ErrorContentBasic appId={appId} />,
        },
      },
      { provider: RecoilRoot },
      { provider: HelmetProviderAsync },
      {
        provider: BuildConfigProvider,
        props: { envVars, mfOutlets },
      },
      { provider: SiteConfigProvider },
      { provider: StorefrontInfoProvider },
      { provider: LocalizationProvider },
      {
        provider: ScopeProvider,
        props: {
          scope: 'x4ec_app',
          fallback: <ErrorContent variant="localized" />,
        },
      },
      { provider: ThemeProvider, props: { appId } },
      {
        provider: ScopeProvider,
        props: { scope: 'x4ec_core' },
      },
      { provider: BrowserRouter, props: { basename } },
      { provider: AuthProvider },
      { provider: InitLocalization },
      { provider: LogoutConfigProvider },
      { provider: FeatureFlagProvider },
    ],
  };
  return providerConfig[appId];
};

// const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({
//   children,
// }) => {
//   useEffect(() => {
//     throw new Error('Error from error provider');
//   }, []);
//   return <>{children}</>;
// };
